import { useId, useMemo, useRef } from 'react'
import { Link } from 'react-router'

import {
  addDays,
  covers,
  dayFormat,
  instantFormat,
  placeItems,
  resolveLocale,
  utcNoon,
  type CalendarDay,
  type CalendarFormat,
  type DatedItem,
  type PlacedItem,
} from './calendar'
import { cn, focusRing } from './cn'
import { CalendarDays } from './icons'
import { useNeedsStacking } from './use-needs-stacking'

export interface WeekAtAGlanceProps extends CalendarFormat {
  readonly title: string
  /** The first of the seven days shown. */
  readonly weekStart: CalendarDay
  readonly items: readonly DatedItem[]
}

/**
 * Seven day columns, a header row over a row of cells, each item a dark block with its time and
 * title that links to it. An item that spans several days shows on each, with its dates.
 */
export function WeekAtAGlance({
  title,
  weekStart,
  items,
  locale: requestedLocale,
  timeZone,
}: WeekAtAGlanceProps) {
  const locale = resolveLocale(requestedLocale)
  const titleId = useId()
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart],
  )
  const placed = useMemo(() => placeItems(items, timeZone), [items, timeZone])
  const boxRef = useRef<HTMLElement>(null)
  const needsStacking = useNeedsStacking(boxRef, placed)

  const rangeLabel = useMemo(
    () =>
      dayFormat(locale, { year: 'numeric', month: 'numeric', day: 'numeric' }).formatRange(
        utcNoon(days[0] ?? weekStart),
        utcNoon(days[6] ?? weekStart),
      ),
    [days, locale, weekStart],
  )
  const weekday = useMemo(() => dayFormat(locale, { weekday: 'long' }), [locale])
  const fullDay = useMemo(
    () => dayFormat(locale, { weekday: 'long', month: 'long', day: 'numeric' }),
    [locale],
  )
  const time = useMemo(
    () => instantFormat({ locale, timeZone }, { hour: 'numeric', minute: '2-digit' }),
    [locale, timeZone],
  )
  const dates = useMemo(
    () => instantFormat({ locale, timeZone }, { month: 'numeric', day: 'numeric' }),
    [locale, timeZone],
  )

  const when = (item: PlacedItem) =>
    item.firstDay === item.lastDay
      ? time.formatRange(item.start, item.end)
      : dates.formatRange(item.start, item.end)

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id={titleId} className="font-heading text-xl font-medium text-text-primary">
          {title}
        </h2>
        <p className="flex items-center gap-1.5 text-sm text-text-secondary">
          <CalendarDays size={16} /> {rangeLabel}
        </p>
      </div>
      {/* Where the room is narrower than seven usable days, as at 320 px or on a zoomed page, the
          days stack, each under its own name, rather than scroll sideways. Until measured it may
          scroll, which the keyboard does once it has focus; relative, so each day's sr-only name
          stays inside it rather than widening the page. */}
      <section
        ref={boxRef}
        aria-labelledby={titleId}
        data-stacked={needsStacking === true ? '' : undefined}
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll region must take focus
        tabIndex={needsStacking === null ? 0 : undefined}
        className={cn('relative overflow-x-auto rounded border border-line stacked:overflow-x-visible', focusRing)}
      >
        {/* Stacked, a long unbroken title wraps within its day rather than widen the page. */}
        <div className="grid min-w-[42rem] grid-cols-7 gap-px bg-divider stacked:min-w-0 stacked:grid-cols-1 stacked:wrap-anywhere">
          {days.map((day) => (
            <div
              key={`${day}-header`}
              aria-hidden="true"
              className="bg-background-contrast-sm px-2 py-2.5 text-center text-sm font-semibold text-text-primary stacked:hidden"
            >
              {weekday.format(utcNoon(day))}
            </div>
          ))}
          {days.map((day) => (
            <div key={day} className="flex min-h-[110px] flex-col gap-1 bg-background p-2 stacked:min-h-0">
              <span className="sr-only stacked:not-sr-only stacked:text-sm stacked:font-semibold stacked:text-text-primary">
                {fullDay.format(utcNoon(day))}
              </span>
              {placed
                .filter((item) => covers(item, day))
                .map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className={cn(
                      'block rounded bg-hard-grey-main px-2 py-1.5 text-xs text-hard-grey-contrast',
                      focusRing,
                    )}
                  >
                    {/* The day in the link's name, so an item on several days reads apart. */}
                    <span className="sr-only">{fullDay.format(utcNoon(day))} </span>
                    <span className="block font-medium">{when(item)}</span>{' '}
                    <span className="block">{item.title}</span>
                  </Link>
                ))}
            </div>
          ))}
        </div>
      </section>
    </section>
  )
}
